/**
 * UsageIndicator Component
 *
 * @description 使用量・制限表示コンポーネント - Issue #286対応
 * @features プログレスバー、警告アラート、制限通知、Stripe連携
 */

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Button,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

/**
 * UsageIndicator Props
 */
interface UsageIndicatorProps {
  /** リソース名 */
  resource: string;
  /** リソースアイコン */
  icon?: React.ReactNode;
  /** 現在の使用量 */
  current: number;
  /** 制限値 */
  limit: number;
  /** 単位（例: "件", "GB", "人"） */
  unit?: string;
  /** 警告閾値（パーセント、デフォルト80%） */
  warningThreshold?: number;
  /** エラー閾値（パーセント、デフォルト95%） */
  errorThreshold?: number;
  /** 表示モード */
  variant?: 'simple' | 'detailed' | 'card';
  /** 読み込み中状態 */
  loading?: boolean;
  /** アップグレードハンドラー */
  onUpgrade?: () => void;
  /** 詳細を表示 */
  showDetails?: boolean;
  /** カスタムメッセージ */
  customMessage?: string;
}

/**
 * 使用率に基づく色を決定
 */
const getUsageColor = (
  percentage: number,
  warningThreshold: number,
  errorThreshold: number
): 'success' | 'warning' | 'error' => {
  if (percentage >= errorThreshold) return 'error';
  if (percentage >= warningThreshold) return 'warning';
  return 'success';
};

/**
 * 使用率に基づくアイコンを取得
 */
const getUsageIcon = (
  percentage: number,
  warningThreshold: number,
  errorThreshold: number
) => {
  if (percentage >= errorThreshold) return <ErrorIcon />;
  if (percentage >= warningThreshold) return <WarningIcon />;
  return <CheckIcon />;
};

/**
 * UsageIndicator Component
 *
 * リソースの使用量を視覚的に表示
 */
export const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  resource,
  icon,
  current,
  limit,
  unit = '',
  warningThreshold = 80,
  errorThreshold = 95,
  variant = 'simple',
  loading = false,
  onUpgrade,
  showDetails = false,
  customMessage,
}) => {
  // 使用率の計算
  const percentage = useMemo(() => {
    if (limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  }, [current, limit]);

  // 状態の決定
  const status = getUsageColor(percentage, warningThreshold, errorThreshold);
  const statusIcon = getUsageIcon(percentage, warningThreshold, errorThreshold);

  // 残量の計算
  const remaining = Math.max(limit - current, 0);
  const isAtLimit = current >= limit;
  const isOverLimit = current > limit;

  // ローディング表示
  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rectangular" height={8} sx={{ my: 1, borderRadius: 1 }} />
        <Skeleton variant="text" width="40%" />
      </Box>
    );
  }

  // シンプル表示
  if (variant === 'simple') {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {icon && <Box component="span" sx={{ mr: 0.5, verticalAlign: 'middle' }}>{icon}</Box>}
            {resource}
          </Typography>
          <Typography variant="body2" fontWeight="medium" color={`${status}.main`}>
            {current.toLocaleString()} / {limit.toLocaleString()} {unit}
          </Typography>
        </Box>

        <Tooltip title={`${percentage.toFixed(1)}% 使用中`}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            color={status}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Tooltip>

        {(status === 'warning' || status === 'error') && (
          <Typography variant="caption" color={`${status}.main`} sx={{ mt: 0.5, display: 'block' }}>
            {isOverLimit
              ? `制限を${(current - limit).toLocaleString()}${unit}超過しています`
              : `残り${remaining.toLocaleString()}${unit}`
            }
          </Typography>
        )}
      </Box>
    );
  }

  // 詳細表示
  if (variant === 'detailed') {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="subtitle1" fontWeight="medium">
              {resource}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${percentage.toFixed(1)}%`}
            color={status}
            icon={statusIcon}
          />
        </Box>

        <LinearProgress
          variant="determinate"
          value={percentage}
          color={status}
          sx={{ height: 10, borderRadius: 5, mb: 2 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              使用中
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {current.toLocaleString()} {unit}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              残量
            </Typography>
            <Typography variant="body1" fontWeight="medium" color={status}>
              {remaining.toLocaleString()} {unit}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              上限
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {limit.toLocaleString()} {unit}
            </Typography>
          </Grid>
        </Grid>

        {(status === 'warning' || status === 'error') && (
          <Alert
            severity={status}
            sx={{ mt: 2 }}
            action={
              onUpgrade && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<TrendingUpIcon fontSize="small" />}
                  onClick={onUpgrade}
                >
                  アップグレード
                </Button>
              )
            }
          >
            {customMessage || (
              isAtLimit
                ? `${resource}の上限に達しています。プランのアップグレードを検討してください。`
                : `${resource}の使用量が${percentage.toFixed(0)}%に達しています。`
            )}
          </Alert>
        )}
      </Box>
    );
  }

  // カード表示
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="h6">
              {resource}
            </Typography>
          </Box>
          <Chip
            label={`${percentage.toFixed(1)}%`}
            color={status}
            icon={statusIcon}
            variant={status === 'success' ? 'outlined' : 'filled'}
          />
        </Box>

        <LinearProgress
          variant="determinate"
          value={percentage}
          color={status}
          sx={{ height: 12, borderRadius: 6, mb: 3 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={status}>
              {current.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              / {limit.toLocaleString()} {unit}
            </Typography>
          </Box>

          {showDetails && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                残り
              </Typography>
              <Typography variant="h6" fontWeight="medium" color={status}>
                {remaining.toLocaleString()} {unit}
              </Typography>
            </Box>
          )}
        </Box>

        {(status === 'warning' || status === 'error') && (
          <Alert
            severity={status}
            icon={statusIcon}
            action={
              onUpgrade && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<TrendingUpIcon />}
                  onClick={onUpgrade}
                >
                  アップグレード
                </Button>
              )
            }
          >
            <AlertTitle>
              {status === 'error' ? '制限に到達' : '使用量が多くなっています'}
            </AlertTitle>
            {customMessage || (
              isOverLimit
                ? `${resource}が制限を超過しています。早急にプランのアップグレードが必要です。`
                : isAtLimit
                ? `${resource}が上限に達しました。これ以上の利用にはアップグレードが必要です。`
                : `${resource}の使用量が${percentage.toFixed(0)}%に達しています。`
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * 複数リソースの使用量サマリー
 */
interface UsageSummaryProps {
  resources: Array<{
    name: string;
    current: number;
    limit: number;
    unit?: string;
    icon?: React.ReactNode;
  }>;
  onUpgrade?: () => void;
  title?: string;
}

export const UsageSummary: React.FC<UsageSummaryProps> = ({
  resources,
  onUpgrade,
  title = '使用量サマリー',
}) => {
  // 最も使用率の高いリソースを特定
  const criticalResource = useMemo(() => {
    return resources.reduce((prev, curr) => {
      const prevPercentage = (prev.current / prev.limit) * 100;
      const currPercentage = (curr.current / curr.limit) * 100;
      return currPercentage > prevPercentage ? curr : prev;
    }, resources[0]);
  }, [resources]);

  const criticalPercentage = (criticalResource.current / criticalResource.limit) * 100;
  const needsUpgrade = criticalPercentage >= 80;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{title}</Typography>
          {needsUpgrade && onUpgrade && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<TrendingUpIcon />}
              onClick={onUpgrade}
            >
              プランを見る
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {resources.map((resource) => (
            <UsageIndicator
              key={resource.name}
              resource={resource.name}
              icon={resource.icon}
              current={resource.current}
              limit={resource.limit}
              unit={resource.unit}
              variant="simple"
            />
          ))}
        </Box>

        {needsUpgrade && (
          <Alert severity="info" sx={{ mt: 3 }} icon={<InfoIcon />}>
            使用量が増えています。より快適にご利用いただくため、プランのアップグレードをご検討ください。
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageIndicator;